import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  KeyboardArrowDownRounded,
  MoreVertRounded,
  RefreshRounded,
  RemoveCircleOutlineRounded,
  RestartAltRounded,
  SearchRounded,
  SpeedOutlined,
  ToggleOnOutlined,
  ToggleOffOutlined,
} from '@mui/icons-material';
import { getAiAccessSettings, resetAiAccessSettings, updateAiAccessSettings } from '../../../../helpers/api';
import { openNotification } from '../../../../helpers/notification';
import { properCase } from '../../../../helpers/utils';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import {
  BRAND,
  USER_CATEGORIES,
  MIN_LIMIT,
  MAX_LIMIT,
  GLOBAL_COPY,
  getScopeHelp,
  formatDateTime,
  cardSx,
  tableSx,
} from './constants';

function DefaultChip() {
  return (
    <Tooltip title="No setting of its own; it follows the broader levels">
      <Chip label="Default" size="small" variant="outlined" sx={{ height: 20, fontSize: 11, fontWeight: 600 }} />
    </Tooltip>
  );
}

function AccessSettingsTable({ scope, noun, singular, paged, feature }) {
  // Every settings call says which feature it is for (ALL, ASSISTANT or AI_REPORT)
  const featureParams = feature ? { feature } : {};
  const globalCopy = GLOBAL_COPY[feature] || GLOBAL_COPY.ALL;
  // Ask AI facilities show the facility's shared count; users show their own uses
  const showUsedToday = scope === 'USER' || (scope === 'FACILITY' && feature === 'ASSISTANT');

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [userCategory, setUserCategory] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [rowBusy, setRowBusy] = useState(null);
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState(null);

  // Close the bulk menu, then run the chosen action
  const runFromMenu = (action) => {
    setBulkMenuAnchor(null);
    action();
  };

  // Limit dialog: { target: 'row' | 'selected' | 'all', item?, value }
  const [limitDialog, setLimitDialog] = useState(null);
  // Confirmation for actions on every item: { title, message, run }
  const [confirm, setConfirm] = useState(null);

  const isGlobal = scope === 'GLOBAL';
  const canSelect = !isGlobal;

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const { data, total: count } = await getAiAccessSettings({
        ...featureParams,
        scope,
        search: paged ? search : undefined,
        userCategory: scope === 'USER' ? userCategory : undefined,
        offset: paged ? page * rowsPerPage : 0,
        limit: paged ? rowsPerPage : 100,
      });
      setItems(data);
      setTotal(count);
    } catch (error) {
      setItems([]);
      setLoadError(error.message);
    } finally {
      setLoading(false);
    }
  }, [scope, paged, search, userCategory, page, rowsPerPage, feature]);

  useEffect(() => {
    load();
  }, [load]);

  // Without a server total, "next" can land on an empty page when the last page was exactly
  // full; step back to the last page that had rows
  useEffect(() => {
    if (!loading && total == null && page > 0 && items.length === 0) setPage((p) => p - 1);
  }, [loading, total, page, items.length]);

  useEffect(() => {
    setSelected([]);
  }, [scope, search, userCategory, page, rowsPerPage]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(0);
      setSearch(searchInput.trim());
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const targetBody = (target, item) => {
    if (isGlobal) return { ...featureParams, scope };
    if (target === 'row') return { ...featureParams, scope, ids: [item.scopeId] };
    if (target === 'selected') return { ...featureParams, scope, ids: selected };
    return {
      ...featureParams,
      scope,
      all: true,
      ...(scope === 'USER' && userCategory ? { userCategory } : {}),
    };
  };

  const runSave = async (request, successMessage) => {
    setSaving(true);
    try {
      const changed = await request();
      const count = typeof changed === 'number' ? changed : null;
      openNotification({
        type: 'success',
        message: count != null ? `${successMessage} (${count} updated)` : successMessage,
      });
      setSelected([]);
      await load();
      return true;
    } catch (error) {
      openNotification({ type: 'error', message: error.message });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const toggleRow = async (item, enabled) => {
    setRowBusy(item.scopeId);
    await runSave(
      () => updateAiAccessSettings({ ...targetBody('row', item), enabled }),
      `AI turned ${enabled ? 'on' : 'off'} for ${item.name}`
    );
    setRowBusy(null);
  };

  const describeTarget = (target) => {
    if (target === 'selected') return `${selected.length} selected ${noun}`;
    if (scope === 'USER' && userCategory) return `every ${properCase(userCategory).toLowerCase()} user`;
    return `every ${singular}`;
  };

  // Bulk actions run on the selection, or on every item when nothing is selected
  const bulkTarget = selected.length ? 'selected' : 'all';

  const withConfirm = (title, message, run) => {
    if (bulkTarget === 'all' && !isGlobal) setConfirm({ title, message, run });
    else run();
  };

  const bulkEnable = (enabled) =>
    withConfirm(
      `Turn AI ${enabled ? 'on' : 'off'} for ${describeTarget('all')}?`,
      `This applies to ${describeTarget('all')}, not just the ones on this page.`,
      () =>
        runSave(
          () => updateAiAccessSettings({ ...targetBody(bulkTarget), enabled }),
          `AI turned ${enabled ? 'on' : 'off'}`
        )
    );

  const bulkClearLimit = () =>
    withConfirm(
      `Remove the daily limit for ${describeTarget('all')}?`,
      'They will follow the broader levels again.',
      () =>
        runSave(
          () => updateAiAccessSettings({ ...targetBody(bulkTarget), clearDailyLimit: true }),
          'Daily limit removed'
        )
    );

  const bulkReset = () =>
    withConfirm(
      `Reset ${describeTarget('all')} to default?`,
      'Their own on/off and limit settings are removed, so they follow the broader levels again.',
      () =>
        runSave(
          () =>
            resetAiAccessSettings(
              bulkTarget === 'selected'
                ? { ...featureParams, scope, ids: selected }
                : { ...featureParams, scope, all: true }
            ),
          'Reset to default'
        )
    );

  const saveLimit = async () => {
    const value = Number(limitDialog.value);
    const ok = await runSave(
      () =>
        updateAiAccessSettings({
          ...targetBody(limitDialog.target, limitDialog.item),
          dailyLimit: value,
        }),
      `Daily limit set to ${value}`
    );
    if (ok) setLimitDialog(null);
  };

  const clearLimitFromDialog = async () => {
    const ok = await runSave(
      () =>
        updateAiAccessSettings({
          ...targetBody(limitDialog.target, limitDialog.item),
          clearDailyLimit: true,
        }),
      'Daily limit removed'
    );
    if (ok) setLimitDialog(null);
  };

  const limitValue = Number(limitDialog?.value);
  const limitValid =
    limitDialog && Number.isInteger(limitValue) && limitValue >= MIN_LIMIT && limitValue <= MAX_LIMIT;

  const pageIds = useMemo(() => items.map((i) => i.scopeId), [items]);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.includes(id));

  if (loadError && !loading) {
    return (
      <Alert
        severity="warning"
        sx={{ borderRadius: '12px' }}
        action={
          <Button color="inherit" size="small" onClick={load}>
            Retry
          </Button>
        }
      >
        {loadError}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {getScopeHelp(scope, feature)}
      </Typography>

      {/* Everyone: a single settings card instead of a one-row table */}
      {isGlobal && !loading && items[0] && (
        <Paper
          elevation={0}
          sx={{ ...cardSx, p: { xs: 3, md: 4 } }}
        >
          <Stack spacing={3} divider={<Divider flexItem />}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  {globalCopy.switchTitle}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {globalCopy.switchHelp}
                </Typography>
              </Box>
              {rowBusy === items[0].scopeId ? (
                <CircularProgress size={22} />
              ) : (
                <Switch
                  checked={Boolean(items[0].enabled)}
                  onChange={(e) => toggleRow(items[0], e.target.checked)}
                  disabled={saving}
                  inputProps={{ 'aria-label': globalCopy.switchTitle }}
                />
              )}
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  {globalCopy.limitTitle}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {globalCopy.limitHelp}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                disabled={saving}
                onClick={() => setLimitDialog({ target: 'row', item: items[0], value: items[0].dailyLimit ?? '' })}
                sx={{ borderRadius: '8px', whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {items[0].dailyLimit != null ? `${items[0].dailyLimit} a day` : globalCopy.limitEmpty}
              </Button>
            </Stack>
            {items[0].updatedAt && (
              <Typography variant="caption" color="text.secondary">
                Last changed {formatDateTime(items[0].updatedAt)}
              </Typography>
            )}
          </Stack>
        </Paper>
      )}

      {/* Filters (left) and bulk actions menu (right) */}
      {!isGlobal && (
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2.5 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          {paged && (
            <TextField
              size="small"
              placeholder={`Search ${noun}`}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 220 }}
            />
          )}
          {scope === 'USER' && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Category</InputLabel>
              <Select
                label="Category"
                value={userCategory}
                onChange={(e) => {
                  setPage(0);
                  setUserCategory(e.target.value);
                }}
              >
                <MenuItem value="">All categories</MenuItem>
                {USER_CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {properCase(c)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={load} disabled={loading}>
                <RefreshRounded />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        {!selected.length && (
          <Button
            variant="outlined"
            endIcon={<KeyboardArrowDownRounded />}
            disabled={saving || loading || !items.length}
            onClick={(e) => setBulkMenuAnchor(e.currentTarget)}
            sx={{ borderRadius: '8px', whiteSpace: 'nowrap', alignSelf: { xs: 'flex-start', sm: 'center' } }}
          >
            Bulk actions
          </Button>
        )}
      </Stack>
      )}

      {/* Selection bar: appears when rows are ticked */}
      {canSelect && selected.length > 0 && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{
            mb: 2,
            px: 2.5,
            py: 1.25,
            borderRadius: '12px',
            bgcolor: 'rgba(12, 91, 171, 0.06)',
            border: '1px solid rgba(12, 91, 171, 0.2)',
          }}
        >
          <Typography variant="body2" fontWeight={700} color={BRAND} sx={{ mr: 'auto' }}>
            {selected.length} selected
            <Button size="small" onClick={() => setSelected([])} sx={{ ml: 1, minWidth: 0, fontWeight: 600 }}>
              Clear
            </Button>
          </Typography>
          <Button size="small" variant="contained" disableElevation disabled={saving} onClick={() => bulkEnable(true)} sx={{ borderRadius: '8px' }}>
            Turn on
          </Button>
          <Button size="small" variant="outlined" color="error" disabled={saving} onClick={() => bulkEnable(false)} sx={{ borderRadius: '8px', bgcolor: '#fff' }}>
            Turn off
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={saving}
            onClick={() => setLimitDialog({ target: 'selected', value: '' })}
            sx={{ borderRadius: '8px', bgcolor: '#fff' }}
          >
            Set limit
          </Button>
          <Tooltip title="More actions">
            <span>
              <IconButton size="small" disabled={saving} onClick={(e) => setBulkMenuAnchor(e.currentTarget)}>
                <MoreVertRounded fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      )}

      {/* Bulk actions menu: applies to the selection, or to every item when nothing is ticked */}
      <Menu
        anchorEl={bulkMenuAnchor}
        open={Boolean(bulkMenuAnchor)}
        onClose={() => setBulkMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { borderRadius: '12px', minWidth: 230, mt: 0.5 } }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 0.5, pb: 1, display: 'block' }}>
          {selected.length ? `For ${selected.length} selected` : `For ${describeTarget('all')}`}
        </Typography>
        {!selected.length && [
          <MenuItem key="on" onClick={() => runFromMenu(() => bulkEnable(true))}>
            <ListItemIcon><ToggleOnOutlined fontSize="small" sx={{ color: '#2e7d32' }} /></ListItemIcon>
            <ListItemText>Turn AI on</ListItemText>
          </MenuItem>,
          <MenuItem key="off" onClick={() => runFromMenu(() => bulkEnable(false))}>
            <ListItemIcon><ToggleOffOutlined fontSize="small" sx={{ color: '#d32f2f' }} /></ListItemIcon>
            <ListItemText>Turn AI off</ListItemText>
          </MenuItem>,
          <MenuItem key="limit" onClick={() => runFromMenu(() => setLimitDialog({ target: 'all', value: '' }))}>
            <ListItemIcon><SpeedOutlined fontSize="small" /></ListItemIcon>
            <ListItemText>Set daily limit…</ListItemText>
          </MenuItem>,
          <Divider key="d1" />,
        ]}
        <MenuItem onClick={() => runFromMenu(bulkClearLimit)}>
          <ListItemIcon><RemoveCircleOutlineRounded fontSize="small" /></ListItemIcon>
          <ListItemText>Remove limit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => runFromMenu(bulkReset)}>
          <ListItemIcon><RestartAltRounded fontSize="small" /></ListItemIcon>
          <ListItemText>Reset to default</ListItemText>
        </MenuItem>
      </Menu>

      {/* List */}
      {isGlobal ? (
        loading && (
          <Paper elevation={0} sx={{ p: 4, border: '1px solid', borderColor: 'divider', borderRadius: '16px' }}>
            <LoaderSkeleton />
          </Paper>
        )
      ) : loading ? (
        <Paper elevation={0} sx={{ p: 4, border: '1px solid', borderColor: 'divider', borderRadius: '16px' }}>
          <LoaderSkeleton />
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={cardSx}>
          <Table sx={tableSx}>
            <TableHead>
              <TableRow>
                {canSelect && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allOnPageSelected}
                      indeterminate={selected.length > 0 && !allOnPageSelected}
                      onChange={(e) => setSelected(e.target.checked ? pageIds : [])}
                    />
                  </TableCell>
                )}
                <TableCell>{isGlobal ? 'Level' : properCase(singular)}</TableCell>
                <TableCell align="center">AI</TableCell>
                <TableCell>Daily limit</TableCell>
                {showUsedToday && <TableCell align="center">Used today</TableCell>}
                <TableCell>Last changed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    No {noun} found
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.scopeId} hover selected={selected.includes(item.scopeId)}>
                    {canSelect && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selected.includes(item.scopeId)}
                          onChange={(e) =>
                            setSelected((prev) =>
                              e.target.checked
                                ? [...prev, item.scopeId]
                                : prev.filter((id) => id !== item.scopeId)
                            )
                          }
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" fontWeight={600}>
                          {scope === 'CATEGORY' ? properCase(item.name) : item.name}
                        </Typography>
                        {!item.customised && <DefaultChip />}
                      </Stack>
                      {item.detail && (
                        <Typography variant="caption" color="text.secondary">
                          {item.detail}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {rowBusy === item.scopeId ? (
                        <CircularProgress size={20} />
                      ) : (
                        <Switch
                          checked={Boolean(item.enabled)}
                          onChange={(e) => toggleRow(item, e.target.checked)}
                          disabled={saving}
                          inputProps={{ 'aria-label': `AI for ${item.name}` }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        disabled={saving}
                        onClick={() =>
                          setLimitDialog({ target: 'row', item, value: item.dailyLimit ?? '' })
                        }
                        sx={{
                          px: 1,
                          minWidth: 0,
                          fontWeight: item.dailyLimit != null ? 700 : 500,
                          color: item.dailyLimit != null ? BRAND : 'text.secondary',
                        }}
                      >
                        {item.dailyLimit != null
                          ? `${item.dailyLimit} a day`
                          : isGlobal
                          ? 'Unlimited'
                          : 'Inherited'}
                      </Button>
                    </TableCell>
                    {showUsedToday && (
                      <TableCell align="center">
                        <Typography variant="body2">{item.usedToday ?? 0}</Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      {item.updatedAt ? (
                        <>
                          <Typography variant="caption" display="block">
                            {formatDateTime(item.updatedAt)}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          Never
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {paged && (
            <TablePagination
              component="div"
              // Server-side paging: use the server's total when it sends one; otherwise allow
              // "next page" while the current page is full ("1–50 of more than 50")
              count={
                total != null
                  ? total
                  : items.length === rowsPerPage
                  ? -1
                  : page * rowsPerPage + items.length
              }
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          )}
        </TableContainer>
      )}

      {/* Daily limit dialog */}
      <Dialog
        open={Boolean(limitDialog)}
        onClose={() => !saving && setLimitDialog(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {limitDialog?.target === 'row'
            ? `Daily limit for ${limitDialog?.item?.name}`
            : `Daily limit for ${describeTarget(limitDialog?.target)}`}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            type="number"
            label="Requests per day"
            value={limitDialog?.value ?? ''}
            onChange={(e) => setLimitDialog((prev) => ({ ...prev, value: e.target.value }))}
            inputProps={{ min: MIN_LIMIT, max: MAX_LIMIT, step: 1 }}
            error={limitDialog?.value !== '' && !limitValid}
            helperText={`Between ${MIN_LIMIT} and ${MAX_LIMIT}. To block AI completely, turn it off instead.`}
            sx={{ mt: 1 }}
          />
          {limitDialog?.target === 'all' && !isGlobal && (
            <Alert severity="info" sx={{ mt: 2, borderRadius: '12px' }}>
              This applies to {describeTarget('all')}, not just the ones on this page.
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            Counts reset at midnight Nigeria time. The most specific level wins: user, then facility, then
            category, then everyone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: 'space-between' }}>
          <Button color="inherit" disabled={saving} onClick={clearLimitFromDialog}>
            Remove limit
          </Button>
          <Stack direction="row" spacing={1}>
            <Button color="inherit" disabled={saving} onClick={() => setLimitDialog(null)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disableElevation
              disabled={saving || !limitValid}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
              onClick={saveLimit}
            >
              Save limit
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      {/* Confirm "apply to all" */}
      <Dialog
        open={Boolean(confirm)}
        onClose={() => !saving && setConfirm(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{confirm?.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {confirm?.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button color="inherit" disabled={saving} onClick={() => setConfirm(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            onClick={async () => {
              await confirm.run();
              setConfirm(null);
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AccessSettingsTable;
